import React, { FC, useContext } from 'react'
import { ChatBox } from '../box/ChatBox'
import styled from '@emotion/styled'
import { ChatDesktopPreview } from './preview/ChatDesktopPreview'
import { ChatDesktopHeader } from './ChatDesktopHeader'
import { useTranslation } from 'react-i18next'
import { ChatDesktopUpload } from './ChatDesktopUpload'
import { ChatQuoteCreateButton } from './ChatQuoteCreateButton'
import { ChatContext } from '../context/ChatContext'
import { ChatDesktopNewChatButton } from './ChatDesktopNewChatButton'
import { ChatManualSupportButton } from '../support/ChatManualSupportButton'
import { PreviewEditsContext } from '../../features/preview-edits/model/PreviewEditsContext'
import { PreviewRecalculateButton } from '../../features/preview-edits/ui/PreviewRecalculateButton'
import { animations } from '../../common/animations'

const Container = styled('div')<{ singleColumn: boolean }>(({ theme, singleColumn }) => ({
  display: 'grid',
  height: '100%',
  gridTemplateColumns: singleColumn ? '1fr' : '340px auto',
  gridTemplateRows: '100%',
  gap: theme.spacing.md,
}))

const Column = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing.sm,
  height: '100%',
}))

const ButtonGroup = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: 8,
})

const Feedback = styled('div')(({ theme }) => ({
  textAlign: 'center',
  fontSize: theme.fontSize.small,
  color: theme.colors.text.muted,
}))

const QuoteHeaderRow = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
})

const RecalculateHint = styled('span')(({ theme }) => ({
  fontSize: theme.fontSize.small,
  color: theme.colors.primary.main,
  fontWeight: theme.fontWeight.normal,
  border: `1px solid ${theme.colors.primary.main}`,
  borderRadius: '0.5em',
  padding: `${theme.spacing.xs}px ${theme.spacing.sm}px`,
  animation: `${animations.pulseGlow} 2s ease-in-out infinite`,
}))

const GlowRecalculateButton = styled(PreviewRecalculateButton)({
  '&:not(:disabled)': {
    animation: `${animations.pulseGlowStrong} 2s ease-in-out infinite`,
    transformOrigin: '80% 50%',
  },
})

export const ChatDesktop: FC = () => {
  const { t } = useTranslation()
  const { preview } = useContext(ChatContext)
  const { dirty, isChatLocked } = useContext(PreviewEditsContext)

  return (
    <Container singleColumn={isChatLocked}>
      {!isChatLocked && (
        <Column>
          <ChatDesktopHeader>
            {t('Get a quote with us')}

            <ChatDesktopNewChatButton />

            <ChatManualSupportButton />
          </ChatDesktopHeader>
          <ChatBox />
          <ChatDesktopUpload />
        </Column>
      )}

      <Column>
        <QuoteHeaderRow>
          <ChatDesktopHeader>
            {t('Your quote')}

            {isChatLocked && <ChatDesktopNewChatButton />}
          </ChatDesktopHeader>

          <span style={{ flex: 1 }} />

          {dirty && (
            <RecalculateHint>
              {t('You have modified an item. Click RECALCULATE to update the missing information')}
            </RecalculateHint>
          )}

          <GlowRecalculateButton disabled={!dirty} big={false} />
        </QuoteHeaderRow>
        <ChatDesktopPreview preview={preview} />

        <ButtonGroup>
          <ChatQuoteCreateButton />
        </ButtonGroup>

        <Feedback>
          {t("Can't find an SKU or do some of them not match?")}{' '}
          <a href="/" target="_blank">
            {t('Contact us')}
          </a>
          .
        </Feedback>
      </Column>
    </Container>
  )
}
